import { useAuthorizeUser } from "@coworker/apprestructured/src/shared/hooks/useAuthorizeUser";
import { SimpleDefaultLoadingScreen } from "@coworker/apprestructured/src/shared/simple/SimpleLoading/SimpleDefaultLoadingScreen";
import { Error } from "@coworker/apprestructured/src/login/components/Error/Error";
import { ErrorType } from "@coworker/apprestructured/src/shared/constants/errorTypes";
import { ReactElement } from "react";
import useFixaLocation from "@coworker/apprestructured/src/shared/wrappers/FixaLocation/useFixaLocation";
import { NewUserPage } from "@coworker/apprestructured/src/login/components/NewUserPage/NewUserPage";
import { UnauthorizedPage } from "@coworker/apprestructured/src/login/components/Unauthorized/UnauthorizedPage";
import {
  useUserHasFixaBasicPermissions,
  useUserHasRoleOnPlatformOwner,
} from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import useFlag, {
  FLAGS,
  useUserPermissionsQuery,
} from "@coworker/app/src/hooks/useFlag";

export const AuthorizedUserRoute = ({
  children,
}: {
  children: ReactElement;
}) => {
  const userState = useAuthorizeUser();
  const { pathname } = useFixaLocation();

  // TO-DO: COAPP-12897 - remove the feature flag once deployed to all markets.
  // Wait for flags to load before evaluating — prevents a flash where the user
  // briefly accesses the app before the flag resolves to true.
  const { isLoading: isFlagLoading } = useUserPermissionsQuery();
  const enableAtkomst = useFlag(FLAGS.ENABLE_ATKOMST);

  // Always check for fixa.basic permission from the Atkomst JWT.
  // Needed for new user self-provisioning: a brand-new Atkomst user has no DB entry yet,
  // so enableAtkomst (which comes from DB-scoped feature flags) is always false for them.
  // Uses MSAL acquireTokenSilent under the hood (may involve a network call on first run)
  // then decodes the JWT locally. staleTime is long to avoid repeated token acquisition.
  const {
    data: hasFixaBasicPermissions,
    isLoading: isFixaBasicPermissionsLoading,
  } = useUserHasFixaBasicPermissions(true);

  const {
    data: hasPlatformOwnerAuthorization,
    isLoading: isPlatformOwnerLoading,
  } = useUserHasRoleOnPlatformOwner();

  if (
    userState.isLoading ||
    isFlagLoading ||
    isFixaBasicPermissionsLoading ||
    isPlatformOwnerLoading
  ) {
    return <SimpleDefaultLoadingScreen />;
  }

  if (enableAtkomst && !hasFixaBasicPermissions && !userState.isError) {
    return <UnauthorizedPage />;
  }

  // Redirect to NewUserPage for users that are not fully provisioned yet (Åtkomst markets only).
  // Guarded by hasFixaBasicPermissions (JWT-derived) rather than enableAtkomst (DB-derived flag)
  // so this works for brand-new Atkomst users who don't have a DB entry yet.
  // Allow /newuserteamselection to pass through so step 2 of the new user flow renders.
  if (hasFixaBasicPermissions && userState.errorType === ErrorType.NOTFOUND) {
    return pathname === "/newuserteamselection" ? children : <NewUserPage />;
  }

  // User has no Atkomst permission and either no DB entry (404) or backend rejected
  // provisioning (403). For users without fixa.basic, show Unauthorized so they can
  // request access via My Identity.
  if (
    !hasFixaBasicPermissions &&
    (userState.errorType === ErrorType.NOTFOUND ||
      (userState.isError && userState.errorType === ErrorType.FORBIDDEN))
  ) {
    return <UnauthorizedPage />;
  }

  // PLATFORM_OWNER users are not PAOM store employees so they always get 403 from the
  // provisioning backend. Send them to NewUserPage to self-select a virtual team store.
  if (
    hasFixaBasicPermissions &&
    hasPlatformOwnerAuthorization &&
    userState.isError &&
    userState.errorType === ErrorType.FORBIDDEN
  ) {
    return pathname === "/newuserteamselection" ? children : <NewUserPage />;
  }

  // User has fixa.basic (valid Atkomst token, possibly cached) but backend cannot provision
  // them — no store assignment in PAOM, or they were removed from Atkomst/DB and the token
  // is still within its validity window. The backend has definitively rejected provisioning,
  // so show UnauthorizedPage regardless of the cached token state.
  if (
    hasFixaBasicPermissions &&
    userState.isError &&
    userState.errorType === ErrorType.FORBIDDEN
  ) {
    return <UnauthorizedPage />;
  }

  if (userState.isError || userState.errorType === ErrorType.FORBIDDEN) {
    return (
      <Error isAccessIssue={userState.errorType === ErrorType.FORBIDDEN} />
    );
  }

  return userState?.isActive ? children : <Error isAccessIssue={false} />;
};
